import { addDays, format, parseISO } from 'date-fns';
import { nanoidGenerator } from 'features/common/helpers';
import { calcularPrecioFinal } from 'features/salesOrder/components/SalesOrderServiceForm/helpers';
import { createNewPassengerGroup } from 'features/salesOrder/components/SalesOrderServiceForm/helpers';
import {
	AccommodationFeeTypeEnum,
	AccommodationRoomBoardEnum,
	accommodationFeeType,
	accommodationRoomBoard,
	BaggageEnum,
	IFlightService,
	IFlightServiceLeg,
	IFeeService,
	IFlightAncillaryService,
	IOtherService,
	ITransferService,
	FlightAncillaryTypeEnum,
	TransferTypeEnum,
	PassengerTypeEnum,
	ServiceUnitEnum,
	TypeServiceEnum,
} from 'features/salesOrder/types';
import {
	Air,
	Destination,
	HotelOptions,
	QuotationFlightInfo,
	RoomBaseConfig,
	RoomFare,
	Service,
	roomRateType,
	roomServiceType,
} from 'features/quotation/types';
import { tariffProviderNotAssignedId } from 'pages/SalesOrder/SalesOrderScreen/constants';
import {
	formatAccommodationDestination,
	mapHotelProviderToAccommodationType,
} from '../helpers/accommodationServicePayload';
import { buildValidatingCarrierForApi } from '../helpers/flightServicePayload';
import {
	LoadedQuotation,
	PassengerSlot,
	WizardSavedPassenger,
	buildPassengerSlots,
	getRoomBaseConfigFromQuotation,
} from '../types';

const boardMap: Record<roomServiceType, AccommodationRoomBoardEnum> = {
	onlyRoom: AccommodationRoomBoardEnum.OnlyRoom,
	breakfast: AccommodationRoomBoardEnum.Breakfast,
	allInclusive: AccommodationRoomBoardEnum.AllInclusive,
	fullPension: AccommodationRoomBoardEnum.FullBoard,
	halfPension: AccommodationRoomBoardEnum.HalfBoard,
};

const rateMap: Record<roomRateType, AccommodationFeeTypeEnum> = {
	refundable: AccommodationFeeTypeEnum.Refundable,
	nonRefundable: AccommodationFeeTypeEnum.NonRefundable,
};

export const getPassengerIdsForRoomIndex = (
	roomIndex: number,
	quotation: LoadedQuotation,
	savedPassengers: WizardSavedPassenger[],
): string[] => {
	const roomConfig = getRoomBaseConfigFromQuotation(quotation);
	const slots = buildPassengerSlots(roomConfig);
	let slotOffset = 0;
	for (let r = 0; r < roomIndex; r += 1) {
		slotOffset += roomConfig[r].adults + roomConfig[r].childs.length + roomConfig[r].infants.length;
	}
	const conf = roomConfig[roomIndex];
	const roomSlotCount = conf.adults + conf.childs.length + conf.infants.length;
	const passengerIds = slots
		.slice(slotOffset, slotOffset + roomSlotCount)
		.map((_, i) => savedPassengers[slotOffset + i]?.keyId)
		.filter((id): id is string => Boolean(id));

	if (passengerIds.length === 0 && savedPassengers.length > 0) {
		return savedPassengers.map((p) => p.keyId);
	}
	return passengerIds;
};

export const formatRoomConfigCaption = (conf: RoomBaseConfig): string => {
	const parts: string[] = [];
	if (conf.adults > 0) {
		parts.push(`${conf.adults} adulto${conf.adults > 1 ? 's' : ''}`);
	}
	if (conf.childs.length > 0) {
		parts.push(`${conf.childs.length} menor${conf.childs.length > 1 ? 'es' : ''}`);
	}
	if (conf.infants.length > 0) {
		parts.push(`${conf.infants.length} infante${conf.infants.length > 1 ? 's' : ''}`);
	}
	return parts.join(' · ') || 'Sin pasajeros';
};

export const getRoomFareLabel = (fare: RoomFare): string => {
	const boardId = boardMap[fare.board];
	const rateId = rateMap[fare.rateType];
	const boardLabel = accommodationRoomBoard.find((b) => b.id === boardId)?.name ?? fare.board;
	const rateLabel = accommodationFeeType.find((r) => r.id === rateId)?.name ?? fare.rateType;
	const desc = fare.description?.trim() || 'Habitación';
	return `${desc} · ${boardLabel} · ${rateLabel} · $${fare.price}`;
};

export type AccommodationRoomInput = {
	roomFare: RoomFare;
	passengerIds: string[];
	cost: number;
};

export const getPassengerIdsByKind = (
	savedPassengers: WizardSavedPassenger[],
	slots: PassengerSlot[],
	kind: PassengerSlot['kind'],
): string[] => savedPassengers.filter((_, index) => slots[index]?.kind === kind).map((p) => p.keyId);

export const mapQuotationLegsToFlightLegs = (data: QuotationFlightInfo[]): IFlightServiceLeg[] =>
	data.map((leg) => ({
		keyId: nanoidGenerator(),
		flightNumber: leg.flightNumber,
		airline: {
			code: leg.airline.code,
			name: leg.airline.name,
			image: leg.airline.image,
		},
		departure: {
			airport: {
				code: leg.departure.airport.code,
				name: leg.departure.airport.name,
				cityName: leg.departure.airport.cityName,
			},
			date: leg.departure.date,
			hour: leg.departure.hour,
		},
		arrival: {
			airport: {
				code: leg.arrival.airport.code,
				name: leg.arrival.airport.name,
				cityName: leg.arrival.airport.cityName,
			},
			date: leg.arrival.date,
			hour: leg.arrival.hour,
		},
	}));

export interface FlightFareOverrides {
	fareADT: number;
	fareCHD: number;
	fareINF: number;
}

export const flightFaresFromAir = (air: Air): FlightFareOverrides => ({
	fareADT: air.fareADT ?? 0,
	fareCHD: air.fareCHD ?? 0,
	fareINF: air.fareINF ?? 0,
});

export const buildFlightPassengerGroups = (
	quotation: LoadedQuotation,
	savedPassengers: WizardSavedPassenger[],
	air: Air,
	fareOverrides?: FlightFareOverrides,
): ReturnType<typeof createNewPassengerGroup>[] => {
	const fares = fareOverrides ?? flightFaresFromAir(air);
	const slots = buildPassengerSlots(getRoomBaseConfigFromQuotation(quotation));
	const markup = quotation.markup ?? 0;
	const groups: ReturnType<typeof createNewPassengerGroup>[] = [];

	const addGroup = (kind: PassengerSlot['kind'], passengerType: PassengerTypeEnum, unitFare: number) => {
		const ids = getPassengerIdsByKind(savedPassengers, slots, kind);
		if (ids.length === 0 || unitFare <= 0) return;

		const unitSale = parseFloat(calcularPrecioFinal(unitFare, markup));
		const group = createNewPassengerGroup(passengerType);
		group.passengerIds = ids;
		group.cost = unitFare;
		group.saleAmount = unitSale;
		group.markupPercentage = markup;
		group.totalCost = unitFare * ids.length;
		group.totalSaleAmount = unitSale * ids.length;
		group.passengersTickets = ids.map((passengerId) => ({
			passengerId,
			ticket: '-',
		}));
		groups.push(group);
	};

	addGroup('adult', PassengerTypeEnum.A, fares.fareADT);
	addGroup('child', PassengerTypeEnum.B, fares.fareCHD);
	addGroup('infant', PassengerTypeEnum.C, fares.fareINF);

	if (groups.length === 0) {
		const ids = savedPassengers.map((p) => p.keyId);
		const unitFare = fares.fareADT || 0;
		const unitSale = parseFloat(calcularPrecioFinal(unitFare, markup));
		const fallback = createNewPassengerGroup(PassengerTypeEnum.A);
		fallback.passengerIds = ids;
		fallback.cost = unitFare;
		fallback.saleAmount = unitSale;
		fallback.markupPercentage = markup;
		fallback.totalCost = unitFare * ids.length;
		fallback.totalSaleAmount = unitSale * ids.length;
		fallback.passengersTickets = ids.map((passengerId) => ({ passengerId, ticket: '-' }));
		groups.push(fallback);
	}

	return groups;
};

export interface BuildFlightServiceInput {
	air: Air;
	quotation: LoadedQuotation;
	savedPassengers: WizardSavedPassenger[];
	flightLegs: IFlightServiceLeg[];
	limitDate: string;
	currency: string;
	reference?: string;
	fareOverrides?: FlightFareOverrides;
	gds?: string;
}

export const buildFlightServiceDraft = ({
	air,
	quotation,
	savedPassengers,
	flightLegs,
	limitDate,
	currency,
	reference = '',
	fareOverrides,
	gds = 'n/a',
}: BuildFlightServiceInput): IFlightService => {
	const markup = String(quotation.markup ?? 0);
	const groups = buildFlightPassengerGroups(quotation, savedPassengers, air, fareOverrides);
	const totalCost = groups.reduce((sum, g) => sum + (g.totalCost || 0), 0);
	const totalSale = groups.reduce((sum, g) => sum + (g.totalSaleAmount || 0), 0);

	return {
		keyId: nanoidGenerator(),
		reference,
		unit: ServiceUnitEnum.Person,
		totalCost: String(totalCost),
		totalSaleAmount: String(Number(totalSale.toFixed(2))),
		currency,
		markupPercentage: markup,
		serviceType: TypeServiceEnum.Flight,
		passengersGroupByPassengerType: groups,
		gds: (gds && gds !== 'n/a' ? gds : 'n/a') as never,
		// @ts-ignore — el API espera un objeto; el tipo declara array (igual que FlightService en pantalla)
		vc: buildValidatingCarrierForApi(air.validatingCarrier),
		oidIssueId: 'n/a',
		limitDate,
		withTst: flightLegs.length > 0 ? (true as never) : (false as never),
		tst: air.tst?.trim() || '',
		tqt: '',
		baggageType: air.baggage === 'none' ? BaggageEnum.None : BaggageEnum.None,
		carryOn: air.carryOn ?? false,
		flightLegs,
		FXL: false,
		OPEROK: false,
		isReissue: false,
		automaticProviderPaymentDateCalculation: true,
		providerManualPaymentDateLines: [],
	};
};

export const buildAccommodationServiceDraft = ({
	destination,
	hotel,
	roomsInput,
	quotation,
	reference,
	tariffProviderId,
}: {
	destination: Destination;
	hotel: HotelOptions;
	roomsInput: AccommodationRoomInput[];
	quotation: LoadedQuotation;
	reference: string;
	tariffProviderId: string;
}): Record<string, unknown> => {
	const markup = quotation.markup ?? 0;
	const startDate = destination.checking;
	const endDate = format(addDays(parseISO(destination.checking), destination.nights), 'yyyy-MM-dd');

	const rooms = roomsInput.map(({ roomFare, passengerIds, cost }) => {
		const saleAmount = parseFloat(calcularPrecioFinal(cost, markup));
		return {
			keyId: nanoidGenerator(),
			passengerIds,
			room: roomFare.description || 'Estandar',
			board: boardMap[roomFare.board] || AccommodationRoomBoardEnum.OnlyRoom,
			tariffType: rateMap[roomFare.rateType] || AccommodationFeeTypeEnum.Refundable,
			cost: String(cost),
			saleAmount: String(saleAmount),
			markupPercentage: String(markup),
		};
	});

	let totalCost = 0;
	let totalSaleAmount = 0;
	rooms.forEach((r) => {
		totalCost += Number(r.cost) || 0;
		totalSaleAmount += Number(r.saleAmount) || 0;
	});

	const accommodationServiceProviderType = mapHotelProviderToAccommodationType(hotel.provider);

	return {
		keyId: nanoidGenerator(),
		reference: reference.trim().toUpperCase(),
		unit: ServiceUnitEnum.PerRoom,
		totalCost: String(totalCost),
		totalSaleAmount: String(totalSaleAmount.toFixed(2)),
		currency: 'USD',
		markupPercentage: String(markup),
		serviceType: TypeServiceEnum.Accommodation,
		destination: formatAccommodationDestination(destination),
		startDate,
		endDate,
		limitDate: startDate,
		rooms,
		sameTariffServiceProvider: false,
		tariffProviderId: tariffProviderId || tariffProviderNotAssignedId,
		serviceProviderId: String(hotel.hotelId),
		accommodationServiceProviderType,
		voucherNotes: '',
		internalNotes: hotel.brokerName ? `Broker: ${hotel.brokerName}` : '',
		automaticProviderPaymentDateCalculation: true,
		providerManualPaymentDateLines: [],
	};
};

export type ExtraServiceItem =
	| { kind: 'destination'; destination: Destination; service: Service; index: number }
	| { kind: 'manual'; service: Service; index: number };

export const collectExtraServices = (quotation: LoadedQuotation): ExtraServiceItem[] => {
	const items: ExtraServiceItem[] = [];
	if ('destination' in quotation && quotation.destination) {
		quotation.destination.forEach((dest) => {
			dest.services?.forEach((service, index) => {
				items.push({ kind: 'destination', destination: dest, service, index });
			});
		});
	}
	if ('manualServices' in quotation && Array.isArray(quotation.manualServices)) {
		quotation.manualServices.forEach((service, index) => {
			items.push({ kind: 'manual', service: service as Service, index });
		});
	}
	return items;
};

export type BuildOtherServiceDraftInput = {
	service: Service;
	quotation: LoadedQuotation;
	startDate: string;
	endDate: string;
	limitDate: string;
	reference: string;
	cost: number;
	otherServiceType: string;
	tariffProviderId: string;
	serviceProviderId: string;
	sameTariffServiceProvider: boolean;
	passengerIds: string[];
};

export const buildOtherServiceDraft = ({
	service,
	quotation,
	startDate,
	endDate,
	limitDate,
	reference,
	cost,
	otherServiceType,
	tariffProviderId,
	serviceProviderId,
	sameTariffServiceProvider,
	passengerIds,
}: BuildOtherServiceDraftInput): IOtherService => {
	const markup = quotation.markup ?? 0;
	const isPerGroup = service.serviceType === 'perGroup';
	const unit = isPerGroup ? ServiceUnitEnum.Group : ServiceUnitEnum.Person;
	const saleAmount = parseFloat(calcularPrecioFinal(cost, markup));

	return {
		keyId: nanoidGenerator(),
		reference,
		tariffProviderId,
		serviceProviderId: sameTariffServiceProvider ? tariffProviderId : serviceProviderId,
		sameTariffServiceProvider,
		unit,
		cost: String(cost),
		saleAmount: String(saleAmount),
		totalCost: String(cost),
		totalSaleAmount: String(saleAmount),
		currency: 'USD',
		markupPercentage: String(markup),
		serviceType: TypeServiceEnum.Other,
		otherServiceType,
		serviceName: service.name,
		startDate,
		endDate,
		limitDate,
		passengerIds,
		internalNotes: service.internalNotes || service.description || '',
		voucherNotes: '',
		automaticProviderPaymentDateCalculation: true,
		providerManualPaymentDateLines: [],
	} as IOtherService;
};

export type BuildTransferServiceDraftInput = {
	quotation: LoadedQuotation;
	reference: string;
	cost: number;
	tariffProviderId: string;
	serviceProviderId: string;
	sameTariffServiceProvider: boolean;
	passengerIds: string[];
	segment: {
		transferType: TransferTypeEnum;
		originName: string;
		destinationName: string;
		startDate: string;
		endDate: string;
	};
};

export const buildTransferServiceDraft = ({
	quotation,
	reference,
	cost,
	tariffProviderId,
	serviceProviderId,
	sameTariffServiceProvider,
	passengerIds,
	segment,
}: BuildTransferServiceDraftInput): ITransferService => {
	const markup = quotation.markup ?? 0;
	const saleAmount = parseFloat(calcularPrecioFinal(cost, markup));
	const isRoundTrip =
		segment.transferType === TransferTypeEnum.InOut || segment.transferType === TransferTypeEnum.InOutPrivate;

	const transferSegment: Record<string, unknown> = {
		transferType: segment.transferType,
		originName: segment.originName,
		destinationName: segment.destinationName,
		startDate: segment.startDate,
	};
	if (isRoundTrip) {
		transferSegment.endDate = segment.endDate;
	}

	return {
		keyId: nanoidGenerator(),
		reference,
		tariffProviderId,
		serviceProviderId: sameTariffServiceProvider ? tariffProviderId : serviceProviderId,
		sameTariffServiceProvider,
		unit: ServiceUnitEnum.Person,
		cost: String(cost),
		saleAmount: String(saleAmount),
		totalCost: String(cost),
		totalSaleAmount: String(saleAmount),
		currency: 'USD',
		markupPercentage: String(markup),
		serviceType: TypeServiceEnum.Transfer,
		passengerIds,
		transferSegments: [transferSegment],
		internalNotes: '',
		voucherNotes: '',
		automaticProviderPaymentDateCalculation: true,
		providerManualPaymentDateLines: [],
	} as ITransferService;
};

export const getValidatingCarrierCodeFromQuotation = (quotation: LoadedQuotation): string => {
	if ('airs' in quotation && quotation.airs?.length) {
		return quotation.airs[0].validatingCarrier?.trim() || '';
	}
	return '';
};

export type BuildFlightAncillaryDraftInput = {
	quotation: LoadedQuotation;
	serviceName: string;
	reference: string;
	cost: number;
	tariffProviderId: string;
	passengerIds: string[];
	limitDate: string;
	flightAncillaryType: FlightAncillaryTypeEnum;
	isPerGroup: boolean;
	validatingCarrierCode?: string;
};

export const suggestFlightAncillaryType = (serviceName: string): FlightAncillaryTypeEnum => {
	const name = serviceName.toLowerCase();
	if (name.includes('equipaje') || name.includes('maleta') || name.includes('baggage') || name.includes('luggage')) {
		return FlightAncillaryTypeEnum.CheckedBaggage;
	}
	if (name.includes('mascota') || name.includes('pet')) {
		return FlightAncillaryTypeEnum.Pet;
	}
	if (name.includes('asiento') || name.includes('seat')) {
		return FlightAncillaryTypeEnum.Seat;
	}
	return FlightAncillaryTypeEnum.SpecialLuggage;
};

export const buildFlightAncillaryServiceDraft = ({
	quotation,
	serviceName,
	reference,
	cost,
	tariffProviderId,
	passengerIds,
	limitDate,
	flightAncillaryType,
	isPerGroup,
	validatingCarrierCode,
}: BuildFlightAncillaryDraftInput): IFlightAncillaryService => {
	const markup = quotation.markup ?? 0;
	const saleAmount = parseFloat(calcularPrecioFinal(cost, markup));
	const vcCode = validatingCarrierCode?.trim() || getValidatingCarrierCodeFromQuotation(quotation) || 'XX';

	return {
		keyId: nanoidGenerator(),
		reference,
		tariffProviderId,
		unit: isPerGroup ? ServiceUnitEnum.Group : ServiceUnitEnum.Person,
		cost: String(cost),
		saleAmount: String(saleAmount),
		totalCost: String(cost),
		totalSaleAmount: String(saleAmount),
		currency: 'USD',
		markupPercentage: String(markup),
		serviceType: TypeServiceEnum.FlightAncillary,
		passengerIds,
		passengersTickets: passengerIds.map((passengerId) => ({ passengerId, ticket: '-' })),
		limitDate,
		flightAncillaryType,
		// @ts-ignore — el API espera un objeto; el tipo declara array (igual que en el formulario de vuelo)
		vc: buildValidatingCarrierForApi(vcCode),
		internalNotes: serviceName,
		automaticProviderPaymentDateCalculation: true,
		providerManualPaymentDateLines: [],
	} as IFlightAncillaryService;
};

export type BuildFeeServiceDraftInput = {
	quotation: LoadedQuotation;
	cost: number;
	tariffProviderId: string;
	passengerIds: string[];
	isPerGroup: boolean;
	feeServiceType?: string;
};

export const buildFeeServiceDraft = ({
	quotation,
	cost,
	tariffProviderId,
	passengerIds,
	isPerGroup,
	feeServiceType = 'fee_por_servicio',
}: BuildFeeServiceDraftInput): IFeeService => {
	const markup = quotation.markup ?? 0;
	const saleAmount = parseFloat(calcularPrecioFinal(cost, markup));

	return {
		keyId: nanoidGenerator(),
		tariffProviderId,
		unit: isPerGroup ? ServiceUnitEnum.Group : ServiceUnitEnum.Person,
		cost: String(cost),
		saleAmount: String(saleAmount),
		totalCost: String(cost),
		totalSaleAmount: String(saleAmount),
		currency: 'USD',
		markupPercentage: String(markup),
		serviceType: TypeServiceEnum.Fee,
		passengerIds,
		feeServiceType,
		automaticProviderPaymentDateCalculation: true,
		providerManualPaymentDateLines: [],
	} as IFeeService;
};
